/**
 * JSONInput Component
 * 
 * A comprehensive JSON input component with real-time validation,
 * syntax highlighting, and clear/reset functionality.
 */

import { JSONValidator } from '../services/JSONValidator.js'
import { JSONBeautifier } from '../services/JSONBeautifier.js'
import { ValidationFeedback } from '../utils/ValidationFeedback.js'
import { SampleDataManager } from '../utils/SampleDataManager.js'
import { JSONHistory } from '../utils/JSONHistory.js'
import { JSONHistoryModal } from './JSONHistoryModal.js'
import { ShareButton } from './ShareButton.js'
import { FileHandler } from '../utils/FileHandler.js'

export class JSONInput {
    constructor(container, options = {}) {
        this.container = container
        this.options = {
            placeholder: 'Paste your JSON here...',
            rows: 15,
            debounceDelay: 300,
            showSuggestions: true,
            enableSyntaxHighlighting: true,
            enableSearch: true,
            enableLocalStorage: true,
            localStorageKey: 'json-viewer-content',
            ...options
        }

        this.textarea = null
        this.validationFeedback = null
        this.syntaxHighlighter = null
        this.shareButton = null
        this.sampleDataManager = null
        this.jsonHistory = new JSONHistory()
        this.historyModal = null
        this.currentValue = ''
        this.isValid = false
        this.lastValidationResult = null
        this.isLoading = false

        // File handling
        this.fileHandler = new FileHandler()
        this.isDragOver = false
        this.dragCounter = 0

        // Search functionality
        this.searchOverlay = null
        this.searchInput = null
        this.searchResults = []
        this.currentSearchIndex = -1
        this.isSearchVisible = false
        this.searchTimeout = null
        this.hasUserNavigated = false

        // Callbacks
        this.onValidJSON = options.onValidJSON || (() => { })
        this.onInvalidJSON = options.onInvalidJSON || (() => { })
        this.onChange = options.onChange || (() => { })

        this.init()
    }

    init() {
        this.render()
        this.setupEventListeners()

        if (this.options.enableSyntaxHighlighting) {
            this.setupSyntaxHighlighting()
        }

        if (this.options.enableSearch) {
            this.setupSearch()
        }

        // Setup drag and drop functionality
        this.setupDragAndDrop()

        if (this.options.enableLocalStorage && !this.hasSharedUrlParameter()) {
            this.loadFromLocalStorage()
        }

        // Initialize sample data manager
        this.sampleDataManager = new SampleDataManager()

        // Add action buttons to toolbar (this sets up the validation and share containers)
        this.addToolbarButtons()

        // Setup validation after toolbar is created
        this.setupValidation()

        // Initialize share button after toolbar is created
        this.setupShareButton()
    }

    render() {
        this.container.innerHTML = `
            <div class="json-input-wrapper">
                <div class="json-input-container">
                    <textarea 
                        class="json-input"
                        placeholder="${this.options.placeholder}"
                        rows="${this.options.rows}"
                        spellcheck="false"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                    ></textarea>
                    ${this.options.enableSyntaxHighlighting ? '<div class="syntax-highlight-overlay"></div>' : ''}
                    <div class="drag-drop-overlay hidden">
                        <div class="drag-drop-content">
                            <div class="drag-drop-icon">📁</div>
                            <div class="drag-drop-text">Drop JSON file here</div>
                            <div class="drag-drop-subtext">Supports .json and .txt files</div>
                        </div>
                    </div>
                </div>
                <div class="validation-feedback-container"></div>
                ${this.options.enableSearch ? `
                <div class="search-overlay hidden">
                    <div class="search-container">
                        <input type="text" class="search-input" placeholder="Search in JSON..." />
                        <div class="search-controls">
                            <button type="button" class="search-nav-btn search-prev" title="Previous match">↑</button>
                            <button type="button" class="search-nav-btn search-next" title="Next match">↓</button>
                            <span class="search-results-count">0 of 0</span>
                            <button type="button" class="search-close-btn" title="Close search">✕</button>
                        </div>
                    </div>
                </div>
                ` : ''}
            </div>
        `

        // Get references to DOM elements
        this.textarea = this.container.querySelector('.json-input')
        this.validationContainer = this.container.querySelector('.validation-feedback-container')
        this.dragDropOverlay = this.container.querySelector('.drag-drop-overlay')

        // Button references and char count will be set up after toolbar buttons are created
        this.clearBtn = null
        this.sampleBtn = null
        this.beautifyBtn = null
        this.searchBtn = null
        this.shareButtonContainer = null
        this.charCount = null

        // Search elements
        if (this.options.enableSearch) {
            this.searchOverlay = this.container.querySelector('.search-overlay')
            this.searchInput = this.container.querySelector('.search-input')
            this.searchPrevBtn = this.container.querySelector('.search-prev')
            this.searchNextBtn = this.container.querySelector('.search-next')
            this.searchResultsCount = this.container.querySelector('.search-results-count')
            this.searchCloseBtn = this.container.querySelector('.search-close-btn')
        }
    }

    setupEventListeners() {
        // Input events
        this.textarea.addEventListener('input', this.handleInput.bind(this))
        this.textarea.addEventListener('paste', this.handlePaste.bind(this))
        this.textarea.addEventListener('keydown', this.handleKeydown.bind(this))

        // Button events will be set up in setupToolbarButtonEvents after buttons are created

        // Focus events for better UX
        this.textarea.addEventListener('focus', this.handleFocus.bind(this))
        this.textarea.addEventListener('blur', this.handleBlur.bind(this))
    }

    setupValidation() {
        if (!this.validationContainer) {
            console.error('Validation container not found - validation feedback will not work')
            return
        }

        this.validationFeedback = new ValidationFeedback(
            this.textarea,
            this.validationContainer,
            {
                debounceDelay: this.options.debounceDelay,
                showSuggestions: this.options.showSuggestions
            }
        )

        // Listen for validation updates
        this.textarea.addEventListener('validationUpdate', (event) => {
            const { result, isValid } = event.detail
            this.lastValidationResult = result
            this.isValid = isValid

            // Update textarea styling
            this.textarea.classList.remove('valid', 'invalid')
            if (result && result.isValid !== undefined) {
                this.textarea.classList.add(isValid ? 'valid' : 'invalid')
            }

            // Call appropriate callback
            if (isValid && result.data) {
                this.onValidJSON(result.data, result)
            } else {
                this.onInvalidJSON(result)
            }

            // Dispatch custom event
            this.dispatchEvent('validation', { result, isValid })
        })

        // Trigger initial validation if there's existing content
        if (this.textarea.value.trim()) {
            this.textarea.dispatchEvent(new Event('input'))
        }
    }

    setupSyntaxHighlighting() {
        if (!this.syntaxOverlay) return

        // Simple syntax highlighting implementation
        this.updateSyntaxHighlighting()
    }

    handleInput(event) {
        const value = event.target.value
        this.currentValue = value

        // Update has-content class for visual feedback
        const inputPanel = this.container.closest('.input-panel')
        if (inputPanel) {
            if (value.trim().length > 0) {
                inputPanel.classList.add('has-content')
            } else {
                inputPanel.classList.remove('has-content')
            }
        }

        // Update character count in validation feedback if available
        if (this.validationFeedback) {
            this.validationFeedback.updateCharacterCount(value.length)
        }

        // Update syntax highlighting
        if (this.options.enableSyntaxHighlighting) {
            this.updateSyntaxHighlighting()
        }

        // Save to localStorage
        this.saveToLocalStorage()

        // Call onChange callback
        this.onChange(value)

        // Dispatch custom event
        this.dispatchEvent('input', { value })
    }

    handlePaste(event) {
        // Allow paste to complete, then validate
        setTimeout(() => {
            this.currentValue = this.textarea.value

            // Update character count in validation feedback if available
            if (this.validationFeedback) {
                this.validationFeedback.updateCharacterCount(this.currentValue.length)
            }

            if (this.options.enableSyntaxHighlighting) {
                this.updateSyntaxHighlighting()
            }

            this.dispatchEvent('paste', { value: this.currentValue })
        }, 0)
    }

    handleKeydown(event) {
        // Handle keyboard shortcuts
        if (event.ctrlKey || event.metaKey) {
            switch (event.key) {
                case 'k':
                    event.preventDefault()
                    this.clear()
                    break
                case 'b':
                    event.preventDefault()
                    this.beautify()
                    break
                case 'm':
                    event.preventDefault()
                    this.minify()
                    break
                case 'f':
                    if (this.options.enableSearch) {
                        event.preventDefault()
                        this.toggleSearch()
                    }
                    break
                case 'h':
                    event.preventDefault()
                    this.showHistory()
                    break
            }
        }

        // Handle escape key to close search
        if (event.key === 'Escape' && this.isSearchVisible) {
            this.closeSearch()
        }

        // Handle tab key for better JSON editing
        if (event.key === 'Tab') {
            event.preventDefault()
            this.insertAtCursor('  ') // Insert 2 spaces
        }
    }

    handleFocus() {
        this.container.classList.add('focused')
        this.dispatchEvent('focus')
    }

    handleBlur() {
        this.container.classList.remove('focused')
        this.dispatchEvent('blur')
    }


    updateSyntaxHighlighting() {
        // Basic function maintained for compatibility - no actual highlighting
        this.currentValue = this.textarea.value
    }

    highlightJSON(text) {
        // Basic JSON syntax highlighting
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="json-string">"$1"</span>')
            .replace(/\b(true|false|null)\b/g, '<span class="json-keyword">$1</span>')
            .replace(/\b(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/g, '<span class="json-number">$1</span>')
            .replace(/([{}[\],:])/g, '<span class="json-punctuation">$1</span>')
    }

    insertAtCursor(text) {
        const start = this.textarea.selectionStart
        const end = this.textarea.selectionEnd
        const value = this.textarea.value

        this.textarea.value = value.substring(0, start) + text + value.substring(end)
        this.textarea.selectionStart = this.textarea.selectionEnd = start + text.length

        // Trigger input event
        this.textarea.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Search functionality
    setupSearch() {
        if (!this.searchInput) return

        // Search input events
        this.searchInput.addEventListener('input', this.handleSearchInput.bind(this))
        this.searchInput.addEventListener('keydown', this.handleSearchKeydown.bind(this))

        // Search navigation events
        this.searchPrevBtn?.addEventListener('click', this.searchPrevious.bind(this))
        this.searchNextBtn?.addEventListener('click', this.searchNext.bind(this))
        this.searchCloseBtn?.addEventListener('click', this.closeSearch.bind(this))

        // Update highlights on scroll and resize
        this.textarea.addEventListener('scroll', this.updateHighlightPositions.bind(this))
        window.addEventListener('resize', this.updateHighlightPositions.bind(this))
    }

    toggleSearch() {
        if (this.isSearchVisible) {
            this.closeSearch()
        } else {
            this.openSearch()
        }
    }

    openSearch() {
        if (!this.searchOverlay) return

        this.searchOverlay.classList.remove('hidden')
        this.isSearchVisible = true
        this.searchInput.focus()

        // If there's selected text, use it as search term
        const selectedText = this.getSelectedText()
        if (selectedText) {
            this.searchInput.value = selectedText
            this.hasUserNavigated = true // User has selected text, so they expect navigation
            this.performSearch(selectedText, true)
        }

        this.dispatchEvent('searchOpen')
    }

    closeSearch() {
        if (!this.searchOverlay) return

        // Clear any pending search timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }

        this.searchOverlay.classList.add('hidden')
        this.isSearchVisible = false
        this.clearSearchHighlights()
        this.searchResults = []
        this.currentSearchIndex = -1
        this.hasUserNavigated = false
        this.updateSearchResultsCount()
        this.textarea.focus()

        this.dispatchEvent('searchClose')
    }

    handleSearchInput(event) {
        const query = event.target.value

        // Clear previous timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }

        // Reset navigation state when user starts typing
        this.hasUserNavigated = false

        // Perform search immediately for empty query (to clear results)
        if (!query.trim()) {
            this.performSearch(query, false)
            return
        }

        // Debounce search for non-empty queries to avoid jumping focus while typing
        this.searchTimeout = setTimeout(() => {
            this.performSearch(query, true)
        }, 150) // Shorter delay for better responsiveness
    }

    handleSearchKeydown(event) {
        switch (event.key) {
            case 'Enter':
                event.preventDefault()
                // If user presses Enter, trigger search with navigation
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout)
                }
                this.hasUserNavigated = true
                this.performSearch(this.searchInput.value, true)
                break
            case 'ArrowDown':
                event.preventDefault()
                this.searchNext()
                break
            case 'ArrowUp':
                event.preventDefault()
                this.searchPrevious()
                break
            case 'Escape':
                this.closeSearch()
                break
        }
    }

    performSearch(query, autoNavigate = true) {
        if (!query.trim()) {
            this.searchResults = []
            this.currentSearchIndex = -1
            this.clearSearchHighlights()
            this.updateSearchResultsCount()
            return
        }

        const text = this.textarea.value
        const regex = new RegExp(this.escapeRegExp(query), 'gi')
        const matches = []
        let match

        while ((match = regex.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                text: match[0]
            })
        }

        this.searchResults = matches
        this.currentSearchIndex = matches.length > 0 ? 0 : -1
        this.updateSearchResultsCount()
        this.highlightSearchResults()

        // Auto-navigate to first match if requested (always for typing, conditional for programmatic calls)
        if (matches.length > 0 && autoNavigate) {
            this.scrollToSearchResult(0)
        }
    }

    searchNext() {
        if (this.searchResults.length === 0) return

        this.hasUserNavigated = true
        this.currentSearchIndex = (this.currentSearchIndex + 1) % this.searchResults.length
        this.highlightSearchResults()
        this.scrollToSearchResult(this.currentSearchIndex)
        this.updateSearchResultsCount()
    }

    searchPrevious() {
        if (this.searchResults.length === 0) return

        this.hasUserNavigated = true
        this.currentSearchIndex = this.currentSearchIndex <= 0
            ? this.searchResults.length - 1
            : this.currentSearchIndex - 1
        this.highlightSearchResults()
        this.scrollToSearchResult(this.currentSearchIndex)
        this.updateSearchResultsCount()
    }

    scrollToSearchResult(index) {
        if (index < 0 || index >= this.searchResults.length) return

        const result = this.searchResults[index]

        // Set selection range
        this.textarea.setSelectionRange(result.start, result.end)

        // Calculate which line the match is on
        const textBeforeMatch = this.textarea.value.substring(0, result.start)
        const linesBeforeMatch = textBeforeMatch.split('\n').length - 1

        // Scroll to make the selection visible
        const lineHeight = parseInt(getComputedStyle(this.textarea).lineHeight) || 20
        const scrollTop = linesBeforeMatch * lineHeight

        this.textarea.scrollTop = scrollTop

        // Don't steal focus from search input - user wants to stay in search box
        // this.textarea.focus() // Commented out to keep focus in search input
    }

    highlightSearchResults() {
        this.clearSearchHighlights()

        if (!this.searchInput?.value.trim() || this.searchResults.length === 0) {
            return
        }

        // Create highlight elements for each search result
        this.searchResults.forEach((result, index) => {
            const isCurrentMatch = index === this.currentSearchIndex
            this.createHighlightElement(result, isCurrentMatch)
        })
    }

    applySearchHighlights(html) {
        if (!this.searchInput?.value.trim() || this.searchResults.length === 0) {
            return html
        }

        const query = this.searchInput.value.trim()
        const escapedQuery = this.escapeRegExp(query)
        const regex = new RegExp(`(${escapedQuery})`, 'gi')

        return html.replace(regex, (match, p1, offset) => {
            const isCurrentMatch = this.searchResults.some((result, index) =>
                index === this.currentSearchIndex &&
                this.textarea.value.substring(result.start, result.end).toLowerCase() === match.toLowerCase()
            )

            const className = isCurrentMatch ? 'search-highlight-current' : 'search-highlight'
            return `<span class="${className}">${match}</span>`
        })
    }

    createHighlightElement(result, isCurrentMatch) {
        const textBeforeMatch = this.textarea.value.substring(0, result.start)
        const lines = textBeforeMatch.split('\n')
        const lineNumber = lines.length - 1
        const columnNumber = lines[lines.length - 1].length

        // Calculate position based on character metrics
        const textMetrics = this.getTextMetrics()
        const lineHeight = textMetrics.lineHeight
        const charWidth = textMetrics.charWidth

        // Create highlight element
        const highlight = document.createElement('div')
        highlight.className = `search-highlight-element ${isCurrentMatch ? 'search-highlight-current-element' : 'search-highlight-normal-element'}`

        // Position the highlight
        const top = lineNumber * lineHeight + this.getTextareaContentTop()
        const left = columnNumber * charWidth + this.getTextareaContentLeft()
        const width = result.text.length * charWidth

        highlight.style.position = 'absolute'
        highlight.style.top = `${top}px`
        highlight.style.left = `${left}px`
        highlight.style.width = `${width}px`
        highlight.style.height = `${lineHeight}px`
        highlight.style.pointerEvents = 'none'
        highlight.style.zIndex = '1'

        // Add to container
        this.container.querySelector('.json-input-container').appendChild(highlight)
    }

    getTextMetrics() {
        // Create a temporary element to measure text metrics
        const temp = document.createElement('div')
        const style = window.getComputedStyle(this.textarea)

        temp.style.position = 'absolute'
        temp.style.visibility = 'hidden'
        temp.style.whiteSpace = 'pre'
        temp.style.font = style.font
        temp.style.fontSize = style.fontSize
        temp.style.fontFamily = style.fontFamily
        temp.style.lineHeight = style.lineHeight
        temp.style.letterSpacing = style.letterSpacing
        temp.style.wordSpacing = style.wordSpacing

        // Use multiple characters to get better average for monospace fonts
        temp.textContent = 'MMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMM'

        document.body.appendChild(temp)
        const charWidth = temp.offsetWidth / 125 // Average character width
        const lineHeight = parseFloat(style.lineHeight) || temp.offsetHeight
        document.body.removeChild(temp)

        return { charWidth, lineHeight }
    }

    getTextareaContentTop() {
        const style = window.getComputedStyle(this.textarea)
        const paddingTop = parseInt(style.paddingTop) || 0
        const borderTop = parseInt(style.borderTopWidth) || 0

        // Get the textarea's position relative to its container
        const textareaRect = this.textarea.getBoundingClientRect()
        const containerRect = this.container.querySelector('.json-input-container').getBoundingClientRect()

        return (textareaRect.top - containerRect.top) + paddingTop + borderTop - this.textarea.scrollTop
    }

    getTextareaContentLeft() {
        const style = window.getComputedStyle(this.textarea)
        const paddingLeft = parseInt(style.paddingLeft) || 0
        const borderLeft = parseInt(style.borderLeftWidth) || 0

        // Get the textarea's position relative to its container
        const textareaRect = this.textarea.getBoundingClientRect()
        const containerRect = this.container.querySelector('.json-input-container').getBoundingClientRect()

        // Add a small adjustment for precise positioning
        return (textareaRect.left - containerRect.left) + paddingLeft + borderLeft - this.textarea.scrollLeft - 3
    }

    updateHighlightPositions() {
        if (!this.isSearchVisible || this.searchResults.length === 0) return

        // Re-create all highlights with updated positions
        this.highlightSearchResults()
    }

    clearSearchHighlights() {
        // Remove all existing highlight elements
        const highlights = this.container.querySelectorAll('.search-highlight-element')
        highlights.forEach(highlight => highlight.remove())
    }

    updateSearchResultsCount() {
        if (!this.searchResultsCount) return

        if (this.searchResults.length === 0) {
            this.searchResultsCount.textContent = '0 of 0'
        } else {
            this.searchResultsCount.textContent = `${this.currentSearchIndex + 1} of ${this.searchResults.length}`
        }
    }

    getSelectedText() {
        const start = this.textarea.selectionStart
        const end = this.textarea.selectionEnd
        return this.textarea.value.substring(start, end)
    }

    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    // Drag and Drop functionality
    setupDragAndDrop() {
        if (!this.textarea || !this.dragDropOverlay) {
            console.warn('Drag and drop elements not found')
            return
        }

        // Prevent default drag behaviors on the document
        document.addEventListener('dragover', this.preventDefaults.bind(this))
        document.addEventListener('drop', this.preventDefaults.bind(this))

        // Add drag and drop event listeners to the textarea and its container
        const inputContainer = this.container.querySelector('.json-input-container')

        // Use both textarea and container for better UX
        const dropTargets = [this.textarea, inputContainer]

        dropTargets.forEach(target => {
            if (target) {
                target.addEventListener('dragenter', this.handleDragEnter.bind(this))
                target.addEventListener('dragover', this.handleDragOver.bind(this))
                target.addEventListener('dragleave', this.handleDragLeave.bind(this))
                target.addEventListener('drop', this.handleDrop.bind(this))
            }
        })
    }

    preventDefaults(event) {
        event.preventDefault()
        event.stopPropagation()
    }

    handleDragEnter(event) {
        this.preventDefaults(event)

        // Increment drag counter to handle nested elements
        this.dragCounter++

        // Only show overlay if files are being dragged
        if (this.fileHandler.hasFiles(event)) {
            this.showDragOverlay()
        }
    }

    handleDragOver(event) {
        this.preventDefaults(event)

        // Check if the dragged items contain files
        if (this.fileHandler.hasFiles(event)) {
            // Set the drop effect
            event.dataTransfer.dropEffect = this.fileHandler.hasValidFiles(event) ? 'copy' : 'none'
            this.showDragOverlay()
        }
    }

    handleDragLeave(event) {
        this.preventDefaults(event)

        // Decrement drag counter
        this.dragCounter--

        // Only hide overlay when we've left all nested elements
        if (this.dragCounter === 0) {
            this.hideDragOverlay()
        }
    }

    async handleDrop(event) {
        this.preventDefaults(event)

        // Reset drag counter
        this.dragCounter = 0
        this.hideDragOverlay()

        // Check if files were dropped
        if (!this.fileHandler.hasFiles(event)) {
            return
        }

        const files = event.dataTransfer.files

        // Show loading state
        this.showLoading('Reading file...')

        try {
            // Process the dropped files
            const result = await this.fileHandler.processFiles(files)

            if (result.success) {
                // Load the file content
                this.setValue(result.content)

                // Show success feedback
                this.showFileDropSuccess(result.fileInfo, result.totalFiles)

                // Dispatch custom event
                this.dispatchEvent('fileDrop', {
                    success: true,
                    fileInfo: result.fileInfo,
                    content: result.content,
                    totalFiles: result.totalFiles
                })
            } else {
                // Show error feedback
                this.showFileDropError(result.error, result.errorType, result.fileInfo)

                // Dispatch custom event
                this.dispatchEvent('fileDropError', {
                    success: false,
                    error: result.error,
                    errorType: result.errorType,
                    fileInfo: result.fileInfo,
                    totalFiles: result.totalFiles
                })
            }
        } catch (error) {
            console.error('Error processing dropped files:', error)
            this.showFileDropError('An unexpected error occurred while processing the file', 'PROCESSING_ERROR')

            this.dispatchEvent('fileDropError', {
                success: false,
                error: error.message,
                errorType: 'PROCESSING_ERROR'
            })
        } finally {
            // Hide loading state
            this.hideLoading()
        }
    }

    showDragOverlay() {
        if (!this.isDragOver && this.dragDropOverlay) {
            this.isDragOver = true
            this.dragDropOverlay.classList.remove('hidden')
            this.container.classList.add('drag-over')

            // Add visual feedback to textarea
            this.textarea.classList.add('drag-over')
        }
    }

    hideDragOverlay() {
        if (this.isDragOver && this.dragDropOverlay) {
            this.isDragOver = false
            this.dragDropOverlay.classList.add('hidden')
            this.container.classList.remove('drag-over')

            // Remove visual feedback from textarea
            this.textarea.classList.remove('drag-over')
        }
    }

    showFileDropSuccess(fileInfo, totalFiles) {
        const fileName = fileInfo ? fileInfo.name : 'file'
        const message = totalFiles > 1
            ? `Successfully loaded "${fileName}" (${totalFiles} files dropped, first valid file used)`
            : `Successfully loaded "${fileName}"`

        this.showNotification(message, 'success', 4000)
    }

    showFileDropError(error, errorType, fileInfo) {
        const userFriendlyError = this.fileHandler.createUserFriendlyError(error, errorType, fileInfo)
        this.showNotification(userFriendlyError, 'error', 6000)
    }

    showNotification(message, type = 'info', duration = 5000) {
        // Create a temporary notification element
        const notification = document.createElement('div')
        notification.className = `file-drop-notification file-drop-notification-${type}`

        // Add icon based on type
        const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'

        notification.innerHTML = `
            <div class="notification-content">
                <span class="notification-icon">${icon}</span>
                <span class="notification-message">${this.escapeHtml(message)}</span>
            </div>
        `

        // Position it near the input area
        const containerRect = this.container.getBoundingClientRect()

        notification.style.position = 'fixed'
        notification.style.top = `${containerRect.top + 10}px`
        notification.style.right = '20px'
        notification.style.zIndex = '1000'
        notification.style.maxWidth = '400px'
        notification.style.opacity = '0'
        notification.style.transform = 'translateX(100%)'
        notification.style.transition = 'all 0.3s ease'

        document.body.appendChild(notification)

        // Animate in
        requestAnimationFrame(() => {
            notification.style.opacity = '1'
            notification.style.transform = 'translateX(0)'
        })

        // Remove after duration
        setTimeout(() => {
            notification.style.opacity = '0'
            notification.style.transform = 'translateX(100%)'
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification)
                }
            }, 300)
        }, duration)
    }

    // Share Button functionality
    setupShareButton() {
        if (!this.shareButtonContainer) {
            console.error('Share button container not found - share functionality will not work')
            return
        }

        this.shareButton = new ShareButton(this.shareButtonContainer, {
            onShareSuccess: (result) => {
                console.log('Share successful:', result)
                this.dispatchEvent('shareSuccess', result)
            },
            onShareError: (error) => {
                console.error('Share failed:', error)
                this.dispatchEvent('shareError', error)
            },
            onLoadingChange: (isLoading) => {
                this.dispatchEvent('shareLoadingChange', { isLoading })
            }
        })

        // Listen for share button events
        this.shareButtonContainer.addEventListener('shareButton:shareSuccess', (event) => {
            console.log('JSON shared successfully:', event.detail)
        })

        this.shareButtonContainer.addEventListener('shareButton:shareError', (event) => {
            console.error('JSON share failed:', event.detail)
        })

        this.shareButtonContainer.addEventListener('shareButton:loadingChange', (event) => {
            const { isLoading } = event.detail
            // Optionally disable other buttons while sharing
            if (this.clearBtn) this.clearBtn.disabled = isLoading
            if (this.beautifyBtn) this.beautifyBtn.disabled = isLoading
            if (this.searchBtn) this.searchBtn.disabled = isLoading
        })
    }

    /**
     * Add action buttons to the input toolbar
     */
    addToolbarButtons() {
        const inputToolbar = document.querySelector('.input-toolbar')
        if (!inputToolbar) {
            console.warn('Input toolbar not found, cannot add action buttons')
            return
        }

        // Create input actions container
        const actionsContainer = document.createElement('div')
        actionsContainer.className = 'input-toolbar-actions'

        // Create action buttons group
        const actionGroup = document.createElement('div')
        actionGroup.className = 'action-buttons-group'

        // Clear button
        const clearBtn = document.createElement('button')
        clearBtn.type = 'button'
        clearBtn.className = 'btn btn-secondary clear-btn'
        clearBtn.innerHTML = '<span class="icon">✕</span><span class="btn-text">Clear</span>'
        clearBtn.title = 'Clear input (Ctrl+K)'
        actionGroup.appendChild(clearBtn)

        // Sample button
        const sampleBtn = document.createElement('button')
        sampleBtn.type = 'button'
        sampleBtn.className = 'btn btn-secondary sample-btn'
        sampleBtn.innerHTML = '<span class="icon">🎲</span><span class="btn-text">Sample</span>'
        sampleBtn.title = 'Load sample data'
        actionGroup.appendChild(sampleBtn)

        // Beautify button
        const beautifyBtn = document.createElement('button')
        beautifyBtn.type = 'button'
        beautifyBtn.className = 'btn btn-secondary beautify-btn'
        beautifyBtn.innerHTML = '<span class="icon">✨</span><span class="btn-text">Beautify</span>'
        beautifyBtn.title = 'Beautify JSON (Ctrl+B)'
        actionGroup.appendChild(beautifyBtn)

        // Minify button
        const minifyBtn = document.createElement('button')
        minifyBtn.type = 'button'
        minifyBtn.className = 'btn btn-secondary minify-btn'
        minifyBtn.innerHTML = '<span class="icon">🗜️</span><span class="btn-text">Minify</span>'
        minifyBtn.title = 'Minify JSON (Ctrl+M)'
        actionGroup.appendChild(minifyBtn)

        // Search button (if enabled)
        if (this.options.enableSearch) {
            const searchBtn = document.createElement('button')
            searchBtn.type = 'button'
            searchBtn.className = 'btn btn-secondary search-btn'
            searchBtn.innerHTML = '<span class="icon">🔍</span><span class="btn-text">Search</span>'
            searchBtn.title = 'Search in JSON (Ctrl+F)'
            actionGroup.appendChild(searchBtn)
        }

        // History button
        const historyBtn = document.createElement('button')
        historyBtn.type = 'button'
        historyBtn.className = 'btn btn-secondary history-btn'
        historyBtn.innerHTML = '<span class="icon">📚</span><span class="btn-text">History</span>'
        historyBtn.title = 'Browse JSON history (Ctrl+H)'
        actionGroup.appendChild(historyBtn)

        // Share button container
        const shareContainer = document.createElement('div')
        shareContainer.className = 'share-button-container'
        actionGroup.appendChild(shareContainer)

        // Add action group to main container
        actionsContainer.appendChild(actionGroup)


        // Add actions container to toolbar
        inputToolbar.appendChild(actionsContainer)

        // Set up button references and event listeners
        this.setupToolbarButtonReferences()
        this.setupToolbarButtonEvents()
    }

    /**
     * Set up references to toolbar buttons
     */
    setupToolbarButtonReferences() {
        const inputToolbar = document.querySelector('.input-toolbar')
        if (!inputToolbar) return

        this.clearBtn = inputToolbar.querySelector('.clear-btn')
        this.sampleBtn = inputToolbar.querySelector('.sample-btn')
        this.beautifyBtn = inputToolbar.querySelector('.beautify-btn')
        this.minifyBtn = inputToolbar.querySelector('.minify-btn')
        this.searchBtn = inputToolbar.querySelector('.search-btn')
        this.historyBtn = inputToolbar.querySelector('.history-btn')
        this.shareButtonContainer = inputToolbar.querySelector('.share-button-container')
    }

    /**
     * Set up event listeners for toolbar buttons
     */
    setupToolbarButtonEvents() {
        if (this.clearBtn) {
            this.clearBtn.addEventListener('click', this.clear.bind(this))
        }

        if (this.sampleBtn) {
            this.sampleBtn.addEventListener('click', this.loadSampleData.bind(this))
        }

        if (this.beautifyBtn) {
            this.beautifyBtn.addEventListener('click', this.beautify.bind(this))
        }

        if (this.minifyBtn) {
            this.minifyBtn.addEventListener('click', this.minify.bind(this))
        }

        if (this.searchBtn) {
            this.searchBtn.addEventListener('click', this.toggleSearch.bind(this))
        }

        if (this.historyBtn) {
            this.historyBtn.addEventListener('click', this.showHistory.bind(this))
        }
    }

    // Local Storage functionality
    saveToLocalStorage() {
        if (!this.options.enableLocalStorage) return

        try {
            // Save to new history system
            const content = this.textarea.value
            if (content && content.trim()) {
                this.jsonHistory.addEntry(content)
            }

            // Keep the old single-entry system for backward compatibility
            const data = {
                content: content,
                timestamp: Date.now()
            }
            localStorage.setItem(this.options.localStorageKey, JSON.stringify(data))
        } catch (error) {
            console.warn('Failed to save to localStorage:', error)
        }
    }

    loadFromLocalStorage() {
        if (!this.options.enableLocalStorage) return

        try {
            const stored = localStorage.getItem(this.options.localStorageKey)
            if (stored) {
                const data = JSON.parse(stored)
                if (data.content && data.content.trim()) {
                    this.setValue(data.content)
                    return true
                }
            }
        } catch (error) {
            console.warn('Failed to load from localStorage:', error)
        }
        return false
    }

    clearLocalStorage() {
        if (!this.options.enableLocalStorage) return

        try {
            localStorage.removeItem(this.options.localStorageKey)
        } catch (error) {
            console.warn('Failed to clear localStorage:', error)
        }
    }

    // Public API methods

    setValue(jsonString) {
        this.textarea.value = jsonString
        this.currentValue = jsonString

        // Update has-content class for visual feedback
        const inputPanel = this.container.closest('.input-panel')
        if (inputPanel) {
            if (jsonString.trim().length > 0) {
                inputPanel.classList.add('has-content')
            } else {
                inputPanel.classList.remove('has-content')
            }
        }

        // Update character count in validation feedback if available
        if (this.validationFeedback) {
            this.validationFeedback.updateCharacterCount(jsonString.length)
        }

        if (this.options.enableSyntaxHighlighting) {
            this.updateSyntaxHighlighting()
        }

        // Trigger validation
        this.textarea.dispatchEvent(new Event('input', { bubbles: true }))

        this.dispatchEvent('setValue', { value: jsonString })
    }

    getValue() {
        return this.textarea.value
    }

    clear() {
        this.setValue('')
        this.textarea.focus()
        this.dispatchEvent('clear')
    }

    /**
     * Show loading state in the input area
     */
    showLoading(message = 'Loading JSON data...') {
        this.isLoading = true

        // Create loading overlay
        const loadingOverlay = document.createElement('div')
        loadingOverlay.className = 'input-loading-overlay'
        loadingOverlay.innerHTML = `
            <div class="input-loading-content">
                <div class="loading-spinner"></div>
                <div class="loading-message">${this.escapeHtml(message)}</div>
            </div>
        `

        // Add overlay to container
        this.container.appendChild(loadingOverlay)

        // Disable textarea
        this.textarea.disabled = true
        this.textarea.style.opacity = '0.5'
    }

    /**
     * Hide loading state
     */
    hideLoading() {
        this.isLoading = false

        // Remove loading overlay
        const loadingOverlay = this.container.querySelector('.input-loading-overlay')
        if (loadingOverlay) {
            loadingOverlay.remove()
        }

        // Re-enable textarea
        this.textarea.disabled = false
        this.textarea.style.opacity = '1'
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    /**
     * Check if there's a shared URL parameter that should take precedence over localStorage
     */
    hasSharedUrlParameter() {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.has('share') || urlParams.has('sample')
    }

    loadSampleData() {
        const sample = this.sampleDataManager.getRandomSample()
        this.setValue(sample.data)
        this.textarea.focus()

        // Show a brief notification about which sample was loaded
        this.showSampleNotification(sample.name, sample.type)

        this.dispatchEvent('sampleLoaded', { sample })
    }

    showSampleNotification(name, type) {
        // Create a temporary notification element
        const notification = document.createElement('div')
        notification.className = 'sample-notification'
        notification.innerHTML = `
            <span class="sample-notification-icon">🎲</span>
            <span class="sample-notification-text">Loaded: ${name} (${type})</span>
        `

        // Position it near the sample button
        const buttonRect = this.sampleBtn.getBoundingClientRect()
        const containerRect = this.container.getBoundingClientRect()

        notification.style.position = 'absolute'
        notification.style.top = `${buttonRect.bottom - containerRect.top + 8}px`
        notification.style.left = `${buttonRect.left - containerRect.left}px`
        notification.style.zIndex = '100'
        notification.style.background = '#f0f9ff'
        notification.style.border = '1px solid #3b82f6'
        notification.style.borderRadius = '6px'
        notification.style.padding = '6px 12px'
        notification.style.fontSize = '12px'
        notification.style.color = '#1e40af'
        notification.style.whiteSpace = 'nowrap'
        notification.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'
        notification.style.opacity = '0'
        notification.style.transform = 'translateY(-4px)'
        notification.style.transition = 'all 0.2s ease'

        this.container.appendChild(notification)

        // Animate in
        requestAnimationFrame(() => {
            notification.style.opacity = '1'
            notification.style.transform = 'translateY(0)'
        })

        // Remove after 2 seconds
        setTimeout(() => {
            notification.style.opacity = '0'
            notification.style.transform = 'translateY(-4px)'
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification)
                }
            }, 200)
        }, 2000)
    }

    beautify() {
        const value = this.getValue()
        if (!value.trim()) return

        const result = JSONBeautifier.beautify(value, {
            indent: 2,
            sortKeys: false,
            insertFinalNewline: false
        })

        if (result.success) {
            this.setValue(result.formatted)
            this.dispatchEvent('beautify', {
                original: value,
                beautified: result.formatted,
                stats: result.stats
            })
        } else {
            // If JSON is invalid, show validation error
            this.validationFeedback?.validate()
            this.dispatchEvent('beautifyError', {
                error: result.error,
                details: result.details
            })
        }
    }

    minify() {
        const value = this.getValue()
        if (!value.trim()) return

        const result = JSONBeautifier.minify(value)

        if (result.success) {
            this.setValue(result.minified)
            this.dispatchEvent('minify', {
                original: value,
                minified: result.minified,
                stats: result.stats
            })
        } else {
            // If JSON is invalid, show validation error
            this.validationFeedback?.validate()
            this.dispatchEvent('minifyError', {
                error: result.error,
                details: result.details
            })
        }
    }

    removeEmptySpace() {
        const value = this.getValue()
        if (!value.trim()) return

        const result = JSONBeautifier.removeEmptySpace(value)

        if (result.success) {
            this.setValue(result.cleaned)
            this.dispatchEvent('removeEmptySpace', {
                original: value,
                cleaned: result.cleaned,
                stats: result.stats
            })
        } else {
            // If JSON is invalid, show validation error
            this.validationFeedback?.validate()
            this.dispatchEvent('removeEmptySpaceError', {
                error: result.error,
                details: result.details
            })
        }
    }

    focus() {
        this.textarea.focus()
    }

    getValidationResult() {
        return this.lastValidationResult
    }

    isValidJSON() {
        return this.isValid
    }

    // Event system
    dispatchEvent(type, detail = {}) {
        const event = new CustomEvent(`jsonInput:${type}`, {
            detail: { ...detail, component: this },
            bubbles: true
        })
        this.container.dispatchEvent(event)
    }

    // History functionality
    /**
     * Show the JSON history modal
     */
    showHistory() {
        if (!this.historyModal) {
            this.historyModal = new JSONHistoryModal({
                onSelect: (entry) => this.loadFromHistory(entry),
                onDelete: (id) => this.handleHistoryDelete(id)
            })
        }
        this.historyModal.show()
    }

    /**
     * Load JSON from history entry
     * @param {Object} entry - History entry
     */
    loadFromHistory(entry) {
        if (entry && entry.content) {
            this.setValue(entry.content)
            this.dispatchEvent('historyLoad', { entry })
        }
    }

    /**
     * Handle history entry deletion
     * @param {string} id - Entry ID or 'all' for clear all
     */
    handleHistoryDelete(id) {
        this.dispatchEvent('historyDelete', { id })
    }

    /**
     * Get history statistics
     * @returns {Object} History statistics
     */
    getHistoryStats() {
        return this.jsonHistory.getStats()
    }

    /**
     * Clear all history
     * @returns {boolean} Success status
     */
    clearHistory() {
        return this.jsonHistory.clearHistory()
    }

    // Cleanup
    destroy() {
        if (this.validationFeedback) {
            this.validationFeedback.destroy()
        }

        if (this.shareButton) {
            this.shareButton.destroy()
        }

        if (this.historyModal) {
            this.historyModal.hide()
            this.historyModal = null
        }

        // Clear any pending search timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }

        // Remove event listeners
        this.textarea?.removeEventListener('input', this.handleInput)
        this.textarea?.removeEventListener('paste', this.handlePaste)
        this.textarea?.removeEventListener('keydown', this.handleKeydown)
        this.textarea?.removeEventListener('scroll', this.updateHighlightPositions)
        window.removeEventListener('resize', this.updateHighlightPositions)
        this.clearBtn?.removeEventListener('click', this.clear)
        this.beautifyBtn?.removeEventListener('click', this.beautify)
        this.minifyBtn?.removeEventListener('click', this.minify)

        // Remove drag and drop event listeners
        document.removeEventListener('dragover', this.preventDefaults)
        document.removeEventListener('drop', this.preventDefaults)

        const inputContainer = this.container.querySelector('.json-input-container')
        const dropTargets = [this.textarea, inputContainer]

        dropTargets.forEach(target => {
            if (target) {
                target.removeEventListener('dragenter', this.handleDragEnter)
                target.removeEventListener('dragover', this.handleDragOver)
                target.removeEventListener('dragleave', this.handleDragLeave)
                target.removeEventListener('drop', this.handleDrop)
            }
        })

        // Clear search highlights
        this.clearSearchHighlights()

        // Clear container
        this.container.innerHTML = ''
    }
}